import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1098QInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title=" IRS Form 1098-Q instructions for Tax Year 2025"
          description="Learn how to fill out IRS Form 1098-Q for Tax Year 2025 with step-by-step guidance from TaxZerone to report distributions from qualified ABLE accounts"
          keywords=""
          canonical="resources/information-returns/form-1098-q-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1098-Q</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Ensure accurate annuity reporting!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098qinstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1098-q-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1098-Q at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Form 1098-Q</strong>
                        {
                          ' reports payments made from qualified longevity annuity contracts (QLACs) held within eligible retirement plans. Issuers use this form to report the fair market value of QLACs and provide information required for retirement plan reporting purposes.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Participant copies
                        </strong>
                        {' must generally be furnished by '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>
                        {' following the calendar year being reported. The '}
                        <strong style={{ fontWeight: 600 }}>
                          IRS filing deadline
                        </strong>
                        {' is '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {'This article covers '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions
                        </strong>
                        {', including issuer and participant details, '}
                        <strong style={{ fontWeight: 600 }}>{'Box 1a '}</strong>
                        (Annuity Amount on Start Date ){', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 1b '}</strong>
                        (Annuity Start Date)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 2 '}</strong>
                        (Check if Start Date May Be Accelerated)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 3 '}</strong>
                        (Total Premiums)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 4 '}</strong>
                        (Fair Market Value of QLAC)
                        {', and '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Boxes 5a through 5l '}
                        </strong>
                        (Premium Payment Amounts and Dates) .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Late, incorrect, or incomplete filing of '}
                        <strong style={{ fontWeight: 600 }}>Form 1098-Q</strong>
                        {' may result in IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the filer meets electronic filing obligations.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies.'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Form 1098-Q, Qualifying Longevity Annuity Contract (QLAC)
                  Information, is an IRS information return used to report
                  details about QLACs purchased from{' '}
                  <span style={{ fontWeight: 600 }}>
                    qualified retirement accounts
                  </span>
                  , such as IRAs and 401(k) plans.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Annuity providers and insurance companies must file this form
                  with the IRS and furnish a copy to the contract holder to
                  ensure compliance with retirement distribution rules. This
                  form helps the IRS track QLAC purchases and verify that the
                  invested amount is correctly excluded from Required Minimum
                  Distributions (RMDs) until annuity payments begin, typically
                  at age 85.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  These instructions provide guidance on who must file, what
                  information must be reported, deadlines, and compliance
                  requirements for Form 1098-Q.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who needs to file form 1098-Q?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Filing requirement for QLAC
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#dead" sx={{ color: '#0202ff' }}>
                          Important deadline to file Form 1098-Q
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key information reported on Form 1098-Q
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form" sx={{ color: '#0202ff' }}>
                          Form 1098-Q instructions-how to fill out?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who needs to file form 1098-Q?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A contract issuer must file Form 1098-Q if they issue a
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    <Link
                      href="/information-returns/efile-irs-form-1098-q/"
                      sx={{ color: '#0202ff' }}
                    >
                      Qualifying Longevity Annuity Contract (QLAC)
                    </Link>
                  </span>
                  . Form 1098-Q is used to report details about QLACs purchased
                  from qualified retirement accounts, such as IRAs and 401(k)
                  plans.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The form provides important information related to QLACs, such
                  as premium payments and annuity terms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="filing"
                >
                  Filing Requirements for a QLAC
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>A QLAC must meet certain requirements, including: </li>
                  <ul>
                    <li>Premiums within dollar limits </li>
                    <li>
                      Annuity payments starting no later than the first day of
                      the month after the employee’s 85th birthday{' '}
                    </li>
                    <li>Specific distribution and death benefit rules </li>
                  </ul>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Limitation on Premiums—Plans and IRAs
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2025 Premium Limit:</span>{' '}
                  For QLACs, premiums paid in 2025 must not exceed $200,000
                  (adjusted for inflation thereafter). This limitation applies
                  to premiums paid for QLACs in both employer-sponsored plans
                  and IRAs.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Exchange of Insurance Contracts for QLAC
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If an insurance contract is exchanged for a QLAC, the{' '}
                  <span style={{ fontWeight: 600 }}>fair market value</span> of
                  the exchanged contract counts towards the premium limit.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Consequences of Excess Premiums
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If premiums exceed the limit, the contract may lose its status
                  as a QLAC unless corrected in a specified manner. The excess
                  must be returned to the non-QLAC portion of the employee’s
                  account.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Death of Employee and Beneficiaries
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Upon the employee’s death, the surviving spouse or designated
                  beneficiary may receive benefits under the QLAC, subject to
                  specific rules regarding annuity payments and survivor
                  benefits.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Return of Premiums
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In the case of death, the return of premium payments to
                  beneficiaries is allowed. However, the return of premium is
                  subject to <span style={{ fontWeight: 600 }}> RMD rules</span>{' '}
                  and must be paid within the required timeframes.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Special Circumstances
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Form 1098-Q must be filed each year, starting from when
                    premiums are first paid, until the contract holder turns 85
                    or passes away.{' '}
                  </li>
                  <li>
                    {' '}
                    If the contract holder dies and the spouse is the sole
                    beneficiary, payments to the spouse don’t start until the
                    holder&apos;s scheduled benefit age.{' '}
                  </li>
                  <li>
                    Continue filing Form 1098-Q annually for the spouse until
                    payments begin, or the spouse passes.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="dead"
                >
                  Important deadline to file Form 1098-Q
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The important due dates for filing, including the{' '}
                  <Link
                    href="/resources/information-returns/form-1098-q-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    deadline Form 1098-Q
                  </Link>
                  , are given below.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  To IRS:
                </Typography>
                <Box component="ol" sx={{ mb: 3.5, '& li': { mb: 1.5 } }}>
                  <li>
                    <b> E-Filing:</b> The IRS encourages the Issuers (annuity
                    providers and insurance companies) to{' '}
                    <Link
                      href="/information-returns/efile-irs-form-1098-q/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      file Form 1098-Q electronically
                    </Link>
                    . Electronic filing is mandatory if the issuer is submitting{' '}
                    <span style={{ fontWeight: 600 }}>10 or more forms</span> in
                    information returns.
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 2, 2026
                      </Typography>
                    </Box>
                  </li>
                  <li>
                    <b> Paper filing:</b> Issuer (Annuity providers and
                    insurance companies) can use the paper filing method for
                    Form 1098-Q if they file{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      fewer than 10 forms.
                    </span>
                    .
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 2, 2026
                      </Typography>
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  To the Participant:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  The issuer (annuity provider or insurance company) must send
                  copies to the{' '}
                  <span style={{ fontWeight: 600 }}>
                    contract holder (participant)
                  </span>{' '}
                  by <span style={{ fontWeight: 600 }}>February 2, 2026</span>,
                  for the{' '}
                  <span style={{ fontWeight: 600 }}> 2025 tax year</span>.
                </Typography>
                <Box component="ul" />
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="key"
                >
                  Key information reported on Form 1098-Q:
                </Typography>
                <Typography variant="body1">
                  Form 1098-Q reports information including:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Contract&apos;s holder information </li>
                  <li>Issuer&apos;s information </li>
                  <li>Account number </li>
                  <li>Annuity start date </li>
                  <li>Annuity invested in QLAC </li>
                  <li>QLAC Certification </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="form"
                >
                  Form 1098-Q instructions-how to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Step-by-step instructions to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-q/"
                    sx={{ color: '#0202ff' }}
                  >
                    fill out Form 1098-Q
                  </Link>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Enter the Issuer’s Details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-q-issuer-details-img.webp" />
                  <source srcSet="/assets/images/f1098-q-issuer-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-issuer-details-img.png"
                    loading="lazy"
                    alt="Step 1: Enter the Issuer’s Details"
                    width={459}
                    height={181}
                    className="img-responsive"
                  />
                </picture>
                <Box sx={{ mb: 2 }}>
                  <picture>
                    <source srcSet="/assets/images/f1098-q-issuer-img.webp" />
                    <source srcSet="/assets/images/f1098-q-issuer-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f1098-q-issuer-img.png"
                      loading="lazy"
                      alt="Step 1: Enter the Issuer’s Details"
                      width={190}
                      height={47}
                      className="img-responsive"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Issuer’s name, address and telephone number along
                  with the Issuer’s Taxpayer identification Number.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Enter the Participant’s Details
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/f1098-q-participant-detail-img.webp" />
                  <source srcSet="/assets/images/f1098-q-participant-detail-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-participant-detail-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Participant’s Details"
                    width={190}
                    height={69}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Participant&apos;s Tax Identification Number (TIN).
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-q-participant-img.webp" />
                  <source srcSet="/assets/images/f1098-q-participant-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-participant-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Participant’s Details"
                    width={459}
                    height={204}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Participant’s name and address including apartment
                  number, city, state, country, and ZIP code.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Qualifying Longevity Annuity Contract Details
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Account Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-q-account-img.webp" />
                  <source srcSet="/assets/images/f1098-q-account-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-account-img.png"
                    loading="lazy"
                    alt="Account Number"
                    width={325}
                    height={69}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  An account number is required if you are filing multiple Forms
                  1098-Q for the same recipient. The IRS also recommends
                  assigning an account number to all Forms 1098-Q in your
                  file.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Plan Number, Name of Plan, and Employer Identification Number
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/f1098-q-plan-number-img.webp" />
                    <source srcSet="/assets/images/f1098-q-plan-number-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f1098-q-plan-number-img.png"
                      loading="lazy"
                      alt="Plan Number, Name of Plan, and Employer Identification Number"
                      width={136}
                      height={69}
                      className="img-responsive"
                    />
                  </picture>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <picture>
                    <source srcSet="/assets/images/f1098-q-name-plan-img.webp" />
                    <source srcSet="/assets/images/f1098-q-name-plan-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f1098-q-name-plan-img.png"
                      loading="lazy"
                      alt="Plan Number, Name of Plan, and Employer Identification Number"
                      width={459}
                      height={69}
                      className="img-responsive"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the contract is purchased under the plan, enter the plan
                  number, name of plan, and EIN of the plan sponsor in the
                  appropriate field.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1a. Annuity Amount on Start Date
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-q-start-date-img.webp" />
                  <source srcSet="/assets/images/f1098-q-start-date-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-start-date-img.png"
                    loading="lazy"
                    alt="Box 1a. Annuity Amount on Start Date"
                    width={190}
                    height={47}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The annuity amount is the scheduled payment that will be made
                  to the participant once the annuity begins.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1b. Annuity Start Date
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-q-annuity-img.webp" />
                  <source srcSet="/assets/images/f1098-q-annuity-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-annuity-img.png"
                    loading="lazy"
                    alt="Box 1b. Annuity Start Date"
                    width={190}
                    height={58}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date when the participant’s annuity payments will
                  begin with their Qualified Longevity Annuity Contract (QLAC).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The date reported is shown in the format of month, day, and
                  year, mm/dd/yyyy.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2. Check if Start Date May Be Accelerated
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-q-accelerated-img.webp" />
                  <source srcSet="/assets/images/f1098-q-accelerated-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-accelerated-img.png"
                    loading="lazy"
                    alt="Box 2. Check if Start Date May Be Accelerated"
                    width={190}
                    height={58}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check this box if annuity payments have not yet started, and
                  the start date can be moved earlier than originally scheduled.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3. Total Premiums
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-q-premiums-img.webp" />
                  <source srcSet="/assets/images/f1098-q-premiums-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-premiums-img.png"
                    loading="lazy"
                    alt="Box 3. Total Premiums"
                    width={190}
                    height={58}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of money that has been paid to the
                  Qualifying Longevity Annuity Contract (QLAC) up until the end
                  of the calendar year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The total amount paid to all QLACs should not exceed $200,000.
                  If exceeds more than that, you should contact the contract
                  issuer.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4. FMV of QLAC
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-q-fmv-img.webp" />
                  <source srcSet="/assets/images/f1098-q-fmv-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-fmv-img.png"
                    loading="lazy"
                    alt="Box 4. FMV of QLAC"
                    width={190}
                    height={58}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Fair Market Value (FMV) of the Qualified Longevity
                  Annuity Contract (QLAC) as of the end of the calendar year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 5a Through 5l
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-q-boxes5-img.webp" />
                  <source srcSet="/assets/images/f1098-q-boxes5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-q-boxes5-img.png"
                    loading="lazy"
                    alt="Boxes 5a Through 5l"
                    width={378}
                    height={271}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the premium amount paid for the contract along with the
                  date of each payment.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on IRS guidelines.
                        For more details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1098q.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          instructions of Form 1098-Q
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          General instructions of Information returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Simplify your Form 1098-Q filing with TaxZerone
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              TaxZerone ensures fast, accurate and secure filing, and helps you
              stay compliant with the IRS.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098qinstructions`}
              >
                Start filing Form 1098-Q Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1098QInstructions;
